import { PlusCircle } from "lucide-react";
import { AvatarForUser } from "./avatar-for-user";
import PersonDialog from "../../modals/person-dialog";
import React, { ReactNode } from "react";
import { Popover, PopoverContent } from "../../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../ui/command";
import { cn } from "../../../lib/utils";
import { Avatar } from "../../ui/avatar";
import { DialogTrigger } from "../../ui/dialog";

interface Person {
    id: string;
    name: string;
    title?: string;
    image?: ReactNode;
}

interface Props {
    users: Person[];
    onSelect?: (id: string) => void;
    onUnselect?: () => void;
    selected?: string;
}

export function UserDropdown(props: React.PropsWithChildren<Props>) {
    const [open, setOpen] = React.useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {props.children}
            <PopoverContent className="w-[200px] p-0" onWheel={(e) => e.stopPropagation()}>
                <Command>
                    <CommandInput placeholder="Search..." />
                    <CommandList>
                        <CommandEmpty>Nothing found.</CommandEmpty>
                        <CommandGroup>
                            {props.users.map((person) => {
                                const isSelected = props.selected === person.id;
                                return (
                                    <CommandItem
                                        className={cn(
                                            "cursor-pointer flex-col items-start transition-all duration-150",
                                            isSelected && "bg-input"
                                        )}
                                        key={person.id}
                                        value={person.name + " " + (person.title ?? "")}
                                        onSelect={() => {
                                            setOpen(false);
                                            if (isSelected) {
                                                props.onUnselect?.();
                                            } else {
                                                props.onSelect?.(person.id);
                                            }
                                        }}
                                    >
                                        <div className={"flex flex-row items-center gap-2"}>
                                            {person.image ? (
                                                <Avatar className={"h-6 w-6"}>{person.image}</Avatar>
                                            ) : (
                                                <AvatarForUser user={person} className="h-6 w-6" />
                                            )}
                                            <div>
                                                {person.name}
                                                <br />
                                                <div className={"text-xs text-muted-foreground"}>{person.title}</div>
                                            </div>
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                    <hr />
                    <CommandGroup>
                        <PersonDialog
                            context={"playbook"}
                            onPersonCreated={(id) => {
                                props.onSelect?.(id);
                                setOpen(false);
                            }}
                        >
                            <DialogTrigger>
                                <CommandItem className={"cursor-pointer gap-3"}>
                                    <PlusCircle className={"h-4 w-4"} />
                                    <div>Add Person</div>
                                </CommandItem>
                            </DialogTrigger>
                        </PersonDialog>
                    </CommandGroup>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
